/**
 * Auto Schema Generator for SEO
 * Generates JSON-LD schema markup for all page types
 */

// Get base URL from environment or default to production
const getBaseUrl = () => {
  if (typeof window !== "undefined") {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}`;
  }
  return "https://primecloudsystems.com";
};

const BASE_URL = getBaseUrl();

/**
 * Generate Organization Schema (used on all pages)
 */
export const generateOrganizationSchema = (baseUrl = BASE_URL) => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${baseUrl}/#organization`,
    name: "Prime Cloud Systems",
    alternateName: "Prime Cloud Systems",
    url: baseUrl,
    logo: {
      "@type": "ImageObject",
      "@id": `${baseUrl}/#logo`,
      url: `${baseUrl}/primecloudsystems_logo.webp`,
      contentUrl: `${baseUrl}/primecloudsystems_logo.webp`,
      width: 4500,
      height: 1500,
      caption: "Prime Cloud Systems Logo",
    },
    description:
      "Prime Cloud Systems is a leading provider of refurbished IT hardware including servers, networking equipment, and computer components.",
    telephone: "+8444211258",
    email: "sales@primecloudsystems.com",
    // address: {
    //   "@type": "PostalAddress",
    //   streetAddress: "25930 Narbonne Ave UNIT 111",
    //   addressLocality: "Lomita",
    //   addressRegion: "CA",
    //   postalCode: "90717",
    //   addressCountry: "US",
    // },

    sameAs: [
      "https://www.facebook.com/primecloudsystems",
      "https://www.twitter.com/primecloudsystems",
      "https://www.linkedin.com/company/primecloudsystems",
    ],
  };
};

/**
 * Generate WebSite Schema
 */
export const generateWebSiteSchema = (baseUrl = BASE_URL) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${baseUrl}/#website`,
    url: baseUrl,
    name: "Prime Cloud Systems",
    alternateName: "Prime Cloud Systems",
    description:
      "Buy servers, PCs, and networking equipment online from a global distributor of end-of-life IT hardware.",
    publisher: {
      "@id": `${baseUrl}/#organization`,
    },
    inLanguage: "en",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${baseUrl}/?s={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
};

/**
 * Generate Breadcrumb Schema
 */
export const generateBreadcrumbSchema = (items, baseUrl = BASE_URL) => {
  const itemListElement = items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${items[items.length - 1]?.url || baseUrl}#breadcrumb`,
    itemListElement,
  };
};

/**
 * Generate WebPage Schema
 */
export const generateWebPageSchema = ({
  url,
  title,
  description,
  image,
  breadcrumbId,
  baseUrl = BASE_URL,
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: {
      "@id": `${baseUrl}/#website`,
    },
    about: {
      "@id": `${baseUrl}/#organization`,
    },
    ...(image && {
      primaryImageOfPage: {
        "@id": `${url}#primaryimage`,
      },
      image: {
        "@id": `${url}#primaryimage`,
      },
    }),
    ...(breadcrumbId && {
      breadcrumb: {
        "@id": breadcrumbId,
      },
    }),
    inLanguage: "en",
  };
};

/**
 * Generate Product Schema
 */
export const generateProductSchema = (product, baseUrl = BASE_URL) => {
  const productUrl = `${baseUrl}/product/${product.product_slug}`;
  const images = [];

  if (product.productImage) {
    const imageBaseUrl = baseUrl.includes("localhost")
      ? `${baseUrl}/primecloudsystems_api/upload`
      : `${baseUrl}/primecloudsystems_api/upload`;
    images.push(`${imageBaseUrl}/${product.productImage}`);
  }

  if (product.productGallery) {
    try {
      const gallery =
        typeof product.productGallery === "string"
          ? JSON.parse(product.productGallery)
          : product.productGallery;
      if (Array.isArray(gallery)) {
        const imageBaseUrl = baseUrl.includes("localhost")
          ? `${baseUrl}/primecloudsystems_api/upload`
          : `${baseUrl}/primecloudsystems_api/upload`;
        gallery.forEach((img) => {
          if (img) images.push(`${imageBaseUrl}/${img}`);
        });
      }
    } catch (e) {
      // Gallery parsing failed
    }
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.productName || "",
    description: product.productShortDesc || product.productDesc || "",
    sku: product.sku || "",
    mpn: product.sku || "",
    ...(product.gtin && { gtin: product.gtin }),
    identifier: product.sku || "",
    brand: {
      "@type": "Brand",
      name: product.brandName || "Prime Cloud Systems",
      ...(product.brand_slug && {
        url: `${baseUrl}/brand/${product.brand_slug}`,
      }),
    },
    category: product.categoryName || "Products",
    url: productUrl,
    ...(images.length > 0 && { image: images }),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: product.rating || "4.5",
      reviewCount: product.review_count || "10",
    },
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "USD",
      price:
        product.regularPrice && !isNaN(product.regularPrice)
          ? parseFloat(product.regularPrice).toFixed(2)
          : "0.00",
      availability:
        product.inStock === "1" || product.availability === "In Stock"
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition:
        product.condition === "New"
          ? "https://schema.org/NewCondition"
          : "https://schema.org/UsedCondition",
      seller: {
        "@type": "Organization",
        name: "Prime Cloud Systems",
        url: baseUrl,
      },
    },
  };

  // Add additional properties
  const additionalProperties = [];
  if (product.condition) {
    additionalProperties.push({
      "@type": "PropertyValue",
      name: "Condition",
      value: product.condition,
    });
  }
  if (product.availability) {
    additionalProperties.push({
      "@type": "PropertyValue",
      name: "Availability",
      value: product.availability,
    });
  }
  if (additionalProperties.length > 0) {
    schema.additionalProperty = additionalProperties;
  }

  return schema;
};

/**
 * Generate CollectionPage Schema (for Shop/Category pages)
 */
export const generateCollectionPageSchema = ({
  url,
  title,
  description,
  categoryName,
  baseUrl = BASE_URL,
}) => {
  return {
    "@context": "https://schema.org",
    "@type": ["WebPage", "CollectionPage"],
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    isPartOf: {
      "@id": `${baseUrl}/#website`,
    },
    about: {
      "@id": `${baseUrl}/#organization`,
    },
    inLanguage: "en",
    ...(categoryName && {
      about: {
        "@type": "ItemList",
        name: categoryName,
      },
    }),
  };
};

/**
 * Generate Service Schema
 */
export const generateServiceSchema = ({
  url,
  title,
  description,
  serviceType,
  baseUrl = BASE_URL,
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: title,
    description,
    provider: {
      "@id": `${baseUrl}/#organization`,
    },
    areaServed: "Worldwide",
    serviceType,
    url,
  };
};

/**
 * Generate FAQ Schema
 */
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

/**
 * Generate complete schema graph for a page
 */
export const generatePageSchema = (pageConfig) => {
  const {
    type = "WebPage",
    url,
    title,
    description,
    image,
    breadcrumbs = [],
    product,
    categoryName,
    serviceType,
    faqs,
    baseUrl = BASE_URL,
  } = pageConfig;

  const schemas = [];

  // Always include Organization and WebSite
  schemas.push(generateOrganizationSchema(baseUrl));
  schemas.push(generateWebSiteSchema(baseUrl));

  // Generate page-specific schema
  switch (type) {
    case "Product":
      if (product) {
        schemas.push(generateProductSchema(product, baseUrl));
      }
      break;

    case "CollectionPage":
      schemas.push(
        generateCollectionPageSchema({
          url,
          title,
          description,
          categoryName,
          baseUrl,
        }),
      );
      break;

    case "Service":
      schemas.push(
        generateServiceSchema({
          url,
          title,
          description,
          serviceType,
          baseUrl,
        }),
      );
      break;

    case "FAQPage":
      if (faqs && faqs.length > 0) {
        schemas.push(generateFAQSchema(faqs));
      }
      break;

    default:
      // WebPage
      schemas.push(
        generateWebPageSchema({
          url,
          title,
          description,
          image,
          breadcrumbId: breadcrumbs.length > 0 ? `${url}#breadcrumb` : null,
          baseUrl,
        }),
      );
  }

  // Add breadcrumb if provided
  if (breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(breadcrumbs, baseUrl));
  }

  // Return as @graph format for better SEO
  if (schemas.length > 1) {
    return {
      "@context": "https://schema.org",
      "@graph": schemas,
    };
  }

  return schemas[0];
};

/**
 * Convert schema object to JSON string for script tag
 */
export const schemaToJsonString = (schema) => {
  return JSON.stringify(schema, null, 2);
};
